import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock3, Download, ExternalLink, ImagePlus, MessageSquareText, MousePointerClick, Pencil, Plus, Printer, QrCode, RefreshCw, Save, ScanLine, Trash2, X } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useMemo, useState } from "react";
import type { ApartmentQrCodeSummary, ApartmentQrContent, ApartmentQrKind } from "@apartos/contracts";
import { api } from "../lib/api";

const kindMeta: Record<ApartmentQrKind, { title: string; description: string; tone: string }> = {
  room_service: {
    title: "Меню в номер",
    description: "Заказ еды и дополнительных услуг прямо в room-service.",
    tone: "orange",
  },
  support: {
    title: "Поддержка",
    description: "Ресепшн, уборка, принадлежности и технические вопросы.",
    tone: "blue",
  },
  city_guide: {
    title: "Гид по городу",
    description: "Места, транспорт, местная кухня и полезные контакты.",
    tone: "purple",
  },
};

export function QrCodesPage({ embedded = false, apartmentId }: { embedded?: boolean; apartmentId?: string }) {
  const [selectedApartment, setSelectedApartment] = useState("all");
  const [editingKind, setEditingKind] = useState<ApartmentQrKind | undefined>("room_service");
  const query = useQuery({ queryKey: ["qr-codes"], queryFn: api.qrCodes });
  const propertyName = "Qadim Yard";
  const requests = useQuery({
    queryKey: ["guest-requests"],
    queryFn: api.guestRequests,
    enabled: !apartmentId,
    refetchInterval: apartmentId ? false : 30_000,
  });
  const client = useQueryClient();
  const regenerate = useMutation({
    mutationFn: ({ apartmentId, qrCodeId }: { apartmentId: string; qrCodeId: string }) =>
      api.regenerateApartmentQrCode(apartmentId, qrCodeId),
    onSuccess: async () => client.invalidateQueries({ queryKey: ["qr-codes"] }),
  });
  const saveContent = useMutation({
    mutationFn: ({ code, content }: { code: ApartmentQrCodeSummary; content: ApartmentQrContent }) =>
      api.updateApartmentQrContent(code.apartmentId, code.id, content),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["qr-codes"] });
    },
  });
  const groups = useMemo(() => {
    const map = new Map<string, ApartmentQrCodeSummary[]>();
    for (const code of (query.data ?? []).filter((item) => !apartmentId || item.apartmentId === apartmentId)) {
      const group = map.get(code.apartmentId) ?? [];
      group.push(code);
      map.set(code.apartmentId, group);
    }
    return [...map.entries()].sort(([, left], [, right]) =>
      (left[0]?.apartmentNumber ?? "").localeCompare(right[0]?.apartmentNumber ?? "", undefined, { numeric: true }));
  }, [apartmentId, query.data]);
  const shown = selectedApartment === "all"
    ? groups : groups.filter(([apartmentId]) => apartmentId === selectedApartment);
  const totals = (query.data ?? []).reduce((result, code) => ({
    scans: result.scans + code.scans,
    requests: result.requests + code.requests,
  }), { scans: 0, requests: 0 });
  const templates = (["room_service", "support", "city_guide"] as ApartmentQrKind[])
    .map((kind) => (query.data ?? []).find((code) => code.kind === kind))
    .filter((code): code is ApartmentQrCodeSummary => Boolean(code));
  const editingTemplate = templates.find((code) => code.kind === editingKind);

  return <div className={`${embedded ? "" : "page "}qr-admin-page ${apartmentId ? "qr-admin-page--apartment" : ""}`}>
    {!apartmentId && <section className="toolbar">
      <div className="qr-summary">
        <span><ScanLine size={15} /> Переходы <strong>{totals.scans}</strong></span>
        <span><MousePointerClick size={15} /> Запросы <strong>{totals.requests}</strong></span>
        <span><QrCode size={15} /> Коды <strong>{query.data?.length ?? 0}</strong></span>
      </div>
      <label className="qr-apartment-filter">Апартамент<select value={selectedApartment} onChange={(event) => setSelectedApartment(event.target.value)}>
        <option value="all">Все апартаменты</option>
        {groups.map(([apartmentId, codes]) => <option value={apartmentId} key={apartmentId}>Apt {codes[0]?.apartmentNumber}</option>)}
      </select></label>
    </section>}
    <div className="form-notice"><QrCode size={17} /><span>{apartmentId ? "QR индивидуальны для этого апартамента, а их меню, поддержка и городской гид управляются общими шаблонами в настройках." : "Коды индивидуальны для каждого апартамента, но содержимое трёх лендингов общее для всего объекта."}</span></div>
    {query.isPending && <div className="card skeleton skeleton--table" />}
    {query.isError && <div className="empty-state"><QrCode size={25} /><h2>QR-коды не загрузились</h2><p>Проверьте соединение с API.</p></div>}
    {!apartmentId && templates.length > 0 && <section className="qr-template-center">
      <header><div><span>КОНСТРУКТОР</span><h2>QR-лендинги для гостей</h2><p>Соберите страницы, которые гость увидит сразу после сканирования.</p></div></header>
      <div className="qr-template-tabs">
        {templates.map((code) => <button className={editingKind === code.kind ? `is-active tone-${kindMeta[code.kind].tone}` : ""} type="button" onClick={() => setEditingKind(code.kind)} aria-expanded={editingKind === code.kind} key={code.kind}>
          <span><Pencil size={14} /><strong>{kindMeta[code.kind].title}</strong></span>
          <small>{code.kind === "room_service" ? `${code.content.services.length} позиций` : code.kind === "support" ? `${code.content.supportCategories.length} тем` : `${code.content.cityGuide.length} мест`}</small>
        </button>)}
      </div>
      {editingTemplate && <QrContentEditor
        key={editingTemplate.kind}
        code={editingTemplate}
        propertyName={propertyName}
        saving={saveContent.isPending}
        error={saveContent.error}
        onClose={() => setEditingKind(undefined)}
        onSave={(content) => saveContent.mutate({ code: editingTemplate, content })}
      />}
    </section>}
    {shown.map(([apartmentId, codes]) => <section className="qr-apartment-group" key={apartmentId}>
      <header><div><span>АПАРТАМЕНТ</span><h2>Apt {codes[0]?.apartmentNumber}</h2></div><button className="secondary-button" onClick={() => printSheet(codes)}><Printer size={15} /> Печатный лист</button></header>
      <div className="qr-card-grid">
        {codes.map((code) => <QrAdminCard code={code} regenerating={regenerate.isPending} onRegenerate={() => {
          if (window.confirm(`Перевыпустить QR «${code.label}» для Apt ${code.apartmentNumber}? Старый QR перестанет работать.`)) {
            regenerate.mutate({ apartmentId: code.apartmentId, qrCodeId: code.id });
          }
        }} key={code.id} />)}
      </div>
    </section>)}
    {!query.isPending && !query.isError && shown.length === 0 && <div className="empty-state"><QrCode size={25} /><h2>Апартаментов пока нет</h2><p>Добавьте апартамент — три QR появятся автоматически.</p></div>}
    {!apartmentId && <section className="qr-request-feed">
      <header><div><span>ЖИВАЯ ЛЕНТА</span><h2>Запросы гостей</h2></div><small><Clock3 size={14} /> Обновляется каждые 30 секунд</small></header>
      <div className="qr-request-list">
        {(requests.data ?? []).map((request) => <article key={request.id}>
          <i className={`qr-request-kind qr-request-kind--${request.kind}`}>{request.kind === "room_service" ? "RS" : request.kind === "support" ? "SP" : "GO"}</i>
          <div><span>{kindMeta[request.kind].title} · Apt {request.apartmentNumber}</span><strong>{request.action}{request.quantity > 1 ? ` × ${request.quantity}` : ""}</strong>{request.message && <p>{request.message}</p>}{request.contact && <small>Контакт: {request.contact}</small>}</div>
          <time dateTime={request.createdAt}>{new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(request.createdAt))}</time>
        </article>)}
        {!requests.isPending && (requests.data?.length ?? 0) === 0 && <div className="qr-request-empty"><MessageSquareText /><strong>Запросов пока нет</strong><span>Первое обращение с QR появится здесь вместе с номером апартамента и источником.</span></div>}
      </div>
    </section>}
  </div>;
}

function QrAdminCard({ code, regenerating, onRegenerate }: {
  code: ApartmentQrCodeSummary; regenerating: boolean; onRegenerate: () => void;
}) {
  const url = `${window.location.origin}${code.publicPath}`;
  const [image, setImage] = useState("");
  useEffect(() => {
    let active = true;
    void QRCode.toDataURL(url, { width: 520, margin: 2, errorCorrectionLevel: "M", color: { dark: "#17171c", light: "#ffffff" } })
      .then((value) => { if (active) setImage(value); });
    return () => { active = false; };
  }, [url]);
  const meta = kindMeta[code.kind];
  return <article className={`qr-admin-card tone-${meta.tone}`}>
    <div className="qr-admin-card__visual">{image ? <img src={image} alt={`QR: ${meta.title}, Apt ${code.apartmentNumber}`} /> : <span className="skeleton" />}</div>
    <div className="qr-admin-card__copy"><span>{code.kind.replace("_", " ")}</span><h3>{code.content.title}</h3><p>{code.content.subtitle}</p></div>
    <div className="qr-admin-card__stats"><span>Сканирования<strong>{code.scans}</strong></span><span>Запросы<strong>{code.requests}</strong></span></div>
    <code title={url}>{url}</code>
    <footer>
      <button className="primary-button" disabled={!image} onClick={() => downloadQr(image, code)}><Download size={14} /> Скачать PNG</button>
      <button className="icon-button" disabled={regenerating} onClick={onRegenerate} title="Перевыпустить QR" aria-label={`Перевыпустить ${meta.title}`}><RefreshCw size={15} /></button>
    </footer>
  </article>;
}

function QrContentEditor({ code, propertyName, saving, error, onClose, onSave }: {
  code: ApartmentQrCodeSummary;
  propertyName: string;
  saving: boolean;
  error: Error | null;
  onClose: () => void;
  onSave: (content: ApartmentQrContent) => void;
}) {
  const [content, setContent] = useState<ApartmentQrContent>(() => structuredClone(code.content));
  const [previewLanguage, setPreviewLanguage] = useState<"ru" | "en">("ru");
  const addId = () => crypto.randomUUID();
  return <form className="qr-content-editor" onSubmit={(event) => { event.preventDefault(); onSave(content); }}>
    <header>
      <div><span>ОБЩИЙ ШАБЛОН</span><h3>{kindMeta[code.kind].title}</h3><p>После публикации изменения появятся во всех апартаментах.</p></div>
      <div><a className="secondary-button" href={code.publicPath} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Предпросмотр</a><button className="icon-button" type="button" onClick={onClose} aria-label="Закрыть редактор"><X size={16} /></button></div>
    </header>
    <div className="qr-builder-layout">
    <div className="qr-builder-controls">
    <div className="qr-content-editor__copy">
      <label className="field"><span>Заголовок · RU</span><input required maxLength={120} value={content.title} onChange={(event) => setContent({ ...content, title: event.target.value })} /></label>
      <label className="field"><span>Описание · RU</span><textarea required maxLength={300} rows={2} value={content.subtitle} onChange={(event) => setContent({ ...content, subtitle: event.target.value })} /></label>
      <label className="field"><span>Page title · EN</span><input maxLength={120} placeholder="English title" value={content.titleEn ?? ""} onChange={(event) => setContent({ ...content, titleEn: event.target.value })} /></label>
      <label className="field"><span>Description · EN</span><textarea maxLength={300} rows={2} placeholder="English description" value={content.subtitleEn ?? ""} onChange={(event) => setContent({ ...content, subtitleEn: event.target.value })} /></label>
    </div>
    {code.kind === "room_service" && <EditorList title="Позиции меню" count={content.services.length} onAdd={() => setContent({ ...content, services: [...content.services, { id: addId(), name: "", nameEn: "", price: 0, description: "", descriptionEn: "", imageUrl: "" }] })}>
      {content.services.map((item) => <div className="qr-editor-row qr-editor-row--service" key={item.id}>
        <ServicePhotoField name={item.name} imageUrl={item.imageUrl} onChange={(imageUrl) => setContent({ ...content, services: content.services.map((entry) => entry.id === item.id ? { ...entry, imageUrl } : entry) })} />
        <label className="field"><span>Название</span><input required maxLength={160} value={item.name} onChange={(event) => setContent({ ...content, services: content.services.map((entry) => entry.id === item.id ? { ...entry, name: event.target.value } : entry) })} /></label>
        <label className="field"><span>Описание</span><input maxLength={300} value={item.description ?? ""} onChange={(event) => setContent({ ...content, services: content.services.map((entry) => entry.id === item.id ? { ...entry, description: event.target.value } : entry) })} /></label>
        <label className="field"><span>Цена, $</span><input required min="0" step="0.01" type="number" value={item.price} onChange={(event) => setContent({ ...content, services: content.services.map((entry) => entry.id === item.id ? { ...entry, price: Number(event.target.value) } : entry) })} /></label>
        <DeleteRow label={item.name || "позицию"} onClick={() => setContent({ ...content, services: content.services.filter((entry) => entry.id !== item.id) })} />
        <div className="qr-editor-translations">
          <label className="field"><span>Name · EN</span><input maxLength={160} placeholder="English name" value={item.nameEn ?? ""} onChange={(event) => setContent({ ...content, services: content.services.map((entry) => entry.id === item.id ? { ...entry, nameEn: event.target.value } : entry) })} /></label>
          <label className="field"><span>Description · EN</span><input maxLength={300} placeholder="English description" value={item.descriptionEn ?? ""} onChange={(event) => setContent({ ...content, services: content.services.map((entry) => entry.id === item.id ? { ...entry, descriptionEn: event.target.value } : entry) })} /></label>
        </div>
      </div>)}
    </EditorList>}
    {code.kind === "support" && <EditorList title="Темы обращений" count={content.supportCategories.length} onAdd={() => setContent({ ...content, supportCategories: [...content.supportCategories, { id: addId(), label: "", urgent: false }] })}>
      {content.supportCategories.map((item) => <div className="qr-editor-row qr-editor-row--support" key={item.id}>
        <label className="field"><span>Название темы</span><input required maxLength={160} value={item.label} onChange={(event) => setContent({ ...content, supportCategories: content.supportCategories.map((entry) => entry.id === item.id ? { ...entry, label: event.target.value } : entry) })} /></label>
        <label className="qr-editor-check"><input type="checkbox" checked={item.urgent ?? false} onChange={(event) => setContent({ ...content, supportCategories: content.supportCategories.map((entry) => entry.id === item.id ? { ...entry, urgent: event.target.checked } : entry) })} /> Приоритетно</label>
        <DeleteRow label={item.label || "тему"} onClick={() => setContent({ ...content, supportCategories: content.supportCategories.filter((entry) => entry.id !== item.id) })} />
        <div className="qr-editor-translations qr-editor-translations--single"><label className="field"><span>Topic name · EN</span><input maxLength={160} placeholder="English topic" value={item.labelEn ?? ""} onChange={(event) => setContent({ ...content, supportCategories: content.supportCategories.map((entry) => entry.id === item.id ? { ...entry, labelEn: event.target.value } : entry) })} /></label></div>
      </div>)}
    </EditorList>}
    {code.kind === "city_guide" && <EditorList title="Места и полезные ссылки" count={content.cityGuide.length} onAdd={() => setContent({ ...content, cityGuide: [...content.cityGuide, { id: addId(), title: "", description: "", category: "", url: "" }] })}>
      {content.cityGuide.map((item) => <div className="qr-editor-row qr-editor-row--guide" key={item.id}>
        <label className="field"><span>Название</span><input required maxLength={160} value={item.title} onChange={(event) => setContent({ ...content, cityGuide: content.cityGuide.map((entry) => entry.id === item.id ? { ...entry, title: event.target.value } : entry) })} /></label>
        <label className="field"><span>Категория</span><input required maxLength={80} value={item.category} onChange={(event) => setContent({ ...content, cityGuide: content.cityGuide.map((entry) => entry.id === item.id ? { ...entry, category: event.target.value } : entry) })} /></label>
        <label className="field field--wide"><span>Описание</span><input required maxLength={500} value={item.description} onChange={(event) => setContent({ ...content, cityGuide: content.cityGuide.map((entry) => entry.id === item.id ? { ...entry, description: event.target.value } : entry) })} /></label>
        <label className="field field--wide"><span>Ссылка</span><input type="url" maxLength={500} placeholder="https://…" value={item.url ?? ""} onChange={(event) => setContent({ ...content, cityGuide: content.cityGuide.map((entry) => entry.id === item.id ? { ...entry, url: event.target.value } : entry) })} /></label>
        <DeleteRow label={item.title || "место"} onClick={() => setContent({ ...content, cityGuide: content.cityGuide.filter((entry) => entry.id !== item.id) })} />
        <div className="qr-editor-translations qr-editor-translations--guide">
          <label className="field"><span>Place name · EN</span><input maxLength={160} placeholder="English name" value={item.titleEn ?? ""} onChange={(event) => setContent({ ...content, cityGuide: content.cityGuide.map((entry) => entry.id === item.id ? { ...entry, titleEn: event.target.value } : entry) })} /></label>
          <label className="field"><span>Category · EN</span><input maxLength={80} placeholder="English category" value={item.categoryEn ?? ""} onChange={(event) => setContent({ ...content, cityGuide: content.cityGuide.map((entry) => entry.id === item.id ? { ...entry, categoryEn: event.target.value } : entry) })} /></label>
          <label className="field field--wide"><span>Description · EN</span><input maxLength={500} placeholder="English description" value={item.descriptionEn ?? ""} onChange={(event) => setContent({ ...content, cityGuide: content.cityGuide.map((entry) => entry.id === item.id ? { ...entry, descriptionEn: event.target.value } : entry) })} /></label>
        </div>
      </div>)}
    </EditorList>}
    </div>
    <QrLandingPreview kind={code.kind} content={content} propertyName={propertyName} language={previewLanguage} onLanguageChange={setPreviewLanguage} />
    </div>
    {error && <div className="form-error">{error.message}</div>}
    <footer><span>Применится ко всем QR типа «{kindMeta[code.kind].title}»</span><button className="primary-button" disabled={saving}><Save size={14} /> {saving ? "Сохраняем…" : "Сохранить для всех апартаментов"}</button></footer>
  </form>;
}

function QrLandingPreview({ kind, content, propertyName, language, onLanguageChange }: { kind: ApartmentQrKind; content: ApartmentQrContent; propertyName: string; language: "ru" | "en"; onLanguageChange: (language: "ru" | "en") => void }) {
  const rows = kind === "room_service"
    ? content.services.slice(0, 4).map((item) => ({ id: item.id, title: (language === "en" ? item.nameEn : item.name) || item.name || "Новая позиция", note: (language === "en" ? item.descriptionEn : item.description) || item.description || (language === "en" ? "Delivery to your apartment" : "Доставка в апартамент"), meta: `$${item.price}`, imageUrl: item.imageUrl }))
    : kind === "support"
      ? content.supportCategories.slice(0, 5).map((item) => ({ id: item.id, title: (language === "en" ? item.labelEn : item.label) || item.label || "Новая тема", note: item.urgent ? (language === "en" ? "Priority" : "Приоритетно") : (language === "en" ? "Reception request" : "Обращение в ресепшн"), meta: "›", imageUrl: undefined }))
      : content.cityGuide.slice(0, 4).map((item) => ({ id: item.id, title: (language === "en" ? item.titleEn : item.title) || item.title || "Новое место", note: (language === "en" ? item.descriptionEn : item.description) || item.description || "Описание места", meta: (language === "en" ? item.categoryEn : item.category) || item.category || "Город", imageUrl: undefined }));
  const title = language === "en" ? content.titleEn || content.title : content.title;
  const subtitle = language === "en" ? content.subtitleEn || content.subtitle : content.subtitle;
  return <aside className="qr-live-preview">
    <div className="qr-live-preview__bar"><span>Живой предпросмотр</span><div className="qr-preview-language"><button type="button" className={language === "ru" ? "is-active" : ""} onClick={() => onLanguageChange("ru")}>RU</button><button type="button" className={language === "en" ? "is-active" : ""} onClick={() => onLanguageChange("en")}>EN</button></div></div>
    <div className="qr-preview-device">
      <div className={`qr-preview-hero qr-preview-hero--${kind}`}>
        <div className="qr-preview-brand"><span><img src="/brand/qadim-yard-logo.png" alt="" /><strong>{propertyName}</strong></span><b>Apt •••</b></div>
        <i>{kind === "room_service" ? "RS" : kind === "support" ? "SP" : "GO"}</i>
        <h4>{title || "Заголовок страницы"}</h4>
        <p>{subtitle || "Описание страницы для гостя"}</p>
      </div>
      <div className="qr-preview-body">
        <header><strong>{kind === "room_service" ? (language === "en" ? "Menu" : "Меню") : kind === "support" ? (language === "en" ? "Choose a topic" : "Выберите тему") : (language === "en" ? "City guide" : "Полезное по городу")}</strong><span>{rows.length}</span></header>
        <div className="qr-preview-items">
          {rows.map((item) => <div className={item.imageUrl ? "has-image" : ""} key={item.id}>{item.imageUrl && <img src={item.imageUrl} alt="" />}<span><strong>{item.title}</strong><small>{item.note}</small></span><b>{item.meta}</b></div>)}
          {rows.length === 0 && <p>Добавьте первый элемент слева — он сразу появится здесь.</p>}
        </div>
        {kind !== "city_guide" && <button type="button" tabIndex={-1}>{kind === "room_service" ? (language === "en" ? "Order" : "Заказать") : (language === "en" ? "Send to reception" : "Отправить на ресепшн")}</button>}
      </div>
      <footer>Запрос будет привязан к номеру гостя</footer>
    </div>
  </aside>;
}

function ServicePhotoField({ name, imageUrl, onChange }: { name: string; imageUrl?: string; onChange: (value: string) => void }) {
  const [error, setError] = useState("");
  return <div className="qr-service-photo">
    <div className="qr-service-photo__preview">
      {imageUrl ? <img src={imageUrl} alt={name ? `Фото: ${name}` : "Фото позиции меню"} /> : <ImagePlus aria-hidden="true" />}
    </div>
    <div>
      <span>Фото продукта</span>
      <div>
        <label className="secondary-button">
          <ImagePlus size={13} /> {imageUrl ? "Заменить" : "Загрузить"}
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={async (event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            try {
              setError("");
              onChange(await prepareMenuPhoto(file));
            } catch (cause) {
              setError(cause instanceof Error ? cause.message : "Не удалось обработать фотографию");
            }
          }} />
        </label>
        {imageUrl && <button className="qr-service-photo__remove" type="button" onClick={() => onChange("")}>Удалить</button>}
      </div>
      {error && <small role="alert">{error}</small>}
    </div>
  </div>;
}

async function prepareMenuPhoto(file: File): Promise<string> {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new Error("Поддерживаются JPG, PNG и WebP");
  if (file.size > 8_000_000) throw new Error("Файл должен быть меньше 8 МБ");
  const source = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Файл изображения повреждён"));
      element.src = source;
    });
    const scale = Math.min(1, 1280 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Браузер не смог обработать изображение");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const result = canvas.toDataURL("image/webp", 0.82);
    if (result.length > 2_000_000) throw new Error("Фото слишком тяжёлое даже после сжатия");
    return result;
  } finally {
    URL.revokeObjectURL(source);
  }
}

function EditorList({ title, count, onAdd, children }: { title: string; count: number; onAdd: () => void; children: React.ReactNode }) {
  return <section className="qr-editor-list"><header><div><h4>{title}</h4><span>{count}</span></div><button className="secondary-button" type="button" onClick={onAdd}><Plus size={14} /> Добавить</button></header><div>{children}</div></section>;
}

function DeleteRow({ label, onClick }: { label: string; onClick: () => void }) {
  return <button className="icon-button qr-editor-delete" type="button" onClick={onClick} aria-label={`Удалить ${label}`} title={`Удалить ${label}`}><Trash2 size={15} /></button>;
}

function downloadQr(image: string, code: ApartmentQrCodeSummary) {
  const anchor = document.createElement("a");
  anchor.href = image;
  anchor.download = `qadim-yard-${code.apartmentNumber}-${code.kind}.png`;
  anchor.click();
}

async function printSheet(codes: ApartmentQrCodeSummary[]) {
  const assets = await Promise.all(codes.map(async (code) => ({
    code,
    image: await QRCode.toDataURL(`${window.location.origin}${code.publicPath}`, {
      width: 720, margin: 2, errorCorrectionLevel: "M",
    }),
  })));
  const popup = window.open("", "_blank");
  if (!popup) return;
  popup.opener = null;
  popup.document.write(`<!doctype html><html><head><title>QR · Apt ${codes[0]?.apartmentNumber}</title><style>
    *{box-sizing:border-box}body{margin:0;padding:18mm;font-family:Arial,sans-serif;color:#17171c}
    h1{margin:0 0 12mm;font-size:22px}.grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8mm}
    article{border:1px solid #d9d9df;border-radius:14px;padding:8mm;text-align:center;break-inside:avoid}
    img{width:100%;max-width:56mm}h2{margin:5mm 0 2mm;font-size:17px}p{margin:0;color:#64646f;font-size:11px;line-height:1.4}
    small{display:block;margin-top:5mm;color:#8b8b95}@media print{body{padding:10mm}}
  </style></head><body><h1>Qadim Yard · Apartment ${escapeHtml(codes[0]?.apartmentNumber ?? "")}</h1><div class="grid">${
    assets.map(({ code, image }) => `<article><img src="${image}"><h2>${escapeHtml(kindMeta[code.kind].title)}</h2><p>${escapeHtml(kindMeta[code.kind].description)}</p><small>Apt ${escapeHtml(code.apartmentNumber)}</small></article>`).join("")
  }</div><script>window.onload=()=>window.print()</script></body></html>`);
  popup.document.close();
}

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (character) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;",
})[character] ?? character);
